// STAGING-LAUNCH-READINESS-002-R2-R2 — sanitized performance telemetry.
// Only ever carries: a generic route name (view.name — never a product id, search
// query, order id, or URL), a lifecycle stage, a coarse duration bucket, and a
// cache hit/miss/stale marker. Never PII, tokens, or raw URLs.
import { emitEvent } from "./events-v2.js";

export type PerfStage = "start" | "ready" | "timeout" | "error";
export type PerfCache = "hit" | "miss" | "stale" | "unknown" | "incremental";

const DURATION_BUCKETS: Array<{ max: number; label: string }> = [
  { max: 150, label: "0-150ms" },
  { max: 500, label: "150-500ms" },
  { max: 1500, label: "500-1500ms" },
  { max: 3000, label: "1500-3000ms" },
  { max: 8000, label: "3000-8000ms" },
];

export function bucketDurationMs(ms: number): string {
  const safe = Number.isFinite(ms) ? Math.max(0, ms) : 0;
  for (const bucket of DURATION_BUCKETS) {
    if (safe < bucket.max) return bucket.label;
  }
  return "8000ms+";
}

export function emitRoutePerf(input: {
  route: string;
  stage: PerfStage;
  durationMs: number;
  cache?: PerfCache;
}): void {
  emitEvent("perf_route_stage", {
    route: input.route,
    stage: input.stage,
    duration_bucket: bucketDurationMs(input.durationMs),
    cache: input.cache ?? "unknown",
  });
  // CUSTOMER-R4.15-B — cheap route lifecycle aliases (no PII).
  if (input.stage === "start") {
    emitEvent("route_load_started", { route: input.route, cache: input.cache ?? "unknown" });
  } else if (input.stage === "ready") {
    emitEvent("route_load_completed", {
      route: input.route,
      duration_bucket: bucketDurationMs(input.durationMs),
      cache: input.cache ?? "unknown",
    });
  } else if (input.stage === "timeout" || input.stage === "error") {
    emitEvent("route_load_failed", {
      route: input.route,
      stage: input.stage,
      duration_bucket: bucketDurationMs(input.durationMs),
    });
  }
}
